import React, { createContext, useEffect, useState } from "react";
import { useErrorContext } from "../../global/hooks/useErrorContext";
import { ILearnerContextType } from "../types/interfaces/ILearnerContextType";
import { ILearnerProviderProps } from "../types/interfaces/ILearnerProviderProps";
import { IIndividualLearningPlan } from "../types/interfaces/IIndividualLearningPlan";
import { useIlpContext } from "../hooks/useIlpContext";
import { IlpData } from "../types/classes/IlpData";

//The ilp context object holds the latest value of the state.
export const LearnerContext = createContext<ILearnerContextType | undefined>(undefined);

//This component is responsible to share the ilp data between components.
export const LearnerProvider: React.FC<ILearnerProviderProps> = ({ children }) => {
    const [individualLearningPlans, setIndividualLearningPlans] = useState<IIndividualLearningPlan[] | null>(null);
    const [individualLearningPlan, setIndividualLearningPlan] = useState<IIndividualLearningPlan | null>(null);
    const [loadingLearnerCtx, setLoadingLearnerCtx] = useState<boolean>(true);
    const { setError } = useErrorContext();
    const { customerKey, role } = useIlpContext();

    const API_GET_ALL_ILPS = process.env.REACT_APP_API_GET_ALL_ILP;
    const INELIGIBLE_ERROR_TEXT: string | null = process.env.REACT_APP_ILP_INELIGIBLE_ERROR_TEXT ?? null;

    const handleError = (message: string | null, statusCode: number) => {
        setError({ message, statusCode });
        setLoadingLearnerCtx(false);
    };

    //Function to fetch data using the get all api endpoint.
    const getAllIlpsFetch = async () => {
        try {
            const apiGetIlps = `${API_GET_ALL_ILPS}${customerKey}`;
            let ilpData = new IlpData<string>(role, null);

            const response = await fetch(apiGetIlps, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(ilpData)
            });
            if (!response.ok) {
                console.log('get all', response.statusText);
                if (response.status === 401 || response.status === 403) {
                    return handleError(INELIGIBLE_ERROR_TEXT, response.status);
                } else {
                    return handleError(null, 1);
                }
            }
            const result = await response.json();
            if (!result) {
                return handleError(null, 2);
            } else {
                if (result.CustomerKey === null || (!result.Succeeded && result.Errors !== null)) {
                    return handleError(null, 3);
                }
               if (result.Data) {
                    setIndividualLearningPlans(result.Data);
                    setIndividualLearningPlan(result.Data[0]);
                }
            }
        } catch (error: any) {
            console.log('get all error', error);
            handleError(null, error.status);
        } finally {
            setLoadingLearnerCtx(false);
        }
    };

    useEffect(() => {
        getAllIlpsFetch();
    }, []);

    const setIlps = async (savedIlp: IIndividualLearningPlan | null) => {
        if (!savedIlp) return;

        setIndividualLearningPlans((prevPlans: IIndividualLearningPlan[] | null) => {

            // Initialize previous plans if null
            if (!prevPlans) {
                setIndividualLearningPlan(savedIlp); // Set as the current ILP if no current ILP is set.
                return [savedIlp];
            }

            // Find index of the existing plan, if any
            const existingPlanIndex = prevPlans.findIndex(plan => plan.IlpKey === savedIlp.IlpKey || (plan.IlpKey === null && prevPlans.filter(plan => plan.IlpKey === null).length === 1));

            // If the plan exists, update it; otherwise, add the new plan
            if (existingPlanIndex !== -1) {
                return prevPlans.map((plan, index) => index === existingPlanIndex ? savedIlp : plan);
            } else {
                return [...prevPlans, savedIlp];
            }
        });
    };

    const deletePlanFromList = (planList: IIndividualLearningPlan[] | null, planKey: string): IIndividualLearningPlan[] | null => {
        if (!planList) return [];

        return planList.filter(plan => plan.IlpKey !== planKey);
    };

    const deletePlan = async (planKey: string) => {
        if (!planKey) return;

        setIndividualLearningPlans((prevPlans: IIndividualLearningPlan[] | null) => {
            return deletePlanFromList(prevPlans, planKey);
        });
    };

    const value = {
        individualLearningPlans,
        individualLearningPlan,
        loadingLearnerCtx,
        customerKey,
        setIndividualLearningPlans,
        setLoadingLearnerCtx,
        setIlps,
        deletePlan
    }

    return (
        <LearnerContext.Provider value={value}>
            {!loadingLearnerCtx && (children)}
        </LearnerContext.Provider>
    );
}